import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, SegmentedControl } from '@usacow/ds';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const ROLES = [
  ['student', 'Student', 'I want to train for USACO'],
  ['coach', 'Coach / teacher', 'I run a class or team'],
  ['parent', 'Parent', 'I support a competitor'],
] as const;

const GOALS = [
  'Promote to the next division',
  'Place in a specific contest',
  'Build consistent reasoning habits',
];

type Division = 'bronze' | 'silver' | 'gold' | 'platinum';

export function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [role, setRole] = useState<string>('student');
  const [goal, setGoal] = useState<string>('');
  const [level, setLevel] = useState<Division>('silver');
  const [saving, setSaving] = useState(false);

  const steps = ['Role', 'Goal', 'Level'];

  async function finish() {
    setSaving(true);
    await api
      .patchProfile({
        role,
        goal_text: goal,
        current_level: level,
        division_goal: level,
        onboarded_at: new Date().toISOString(),
      })
      .catch(() => {});
    await refresh();
    navigate('/app/diagnostic');
  }

  return (
    <div className="auth">
      <div className="auth-card auth-card--wide">
        <div className="onb-steps">
          {steps.map((s, i) => (
            <span
              key={s}
              className={`onb-step${i === step ? ' is-on' : ''}${i < step ? ' is-done' : ''}`}
            >
              {s}
            </span>
          ))}
        </div>

        {step === 0 && (
          <div className="onb-body">
            <h2 className="auth-h">Who are you?</h2>
            <div className="onb-roles">
              {ROLES.map(([k, t, d]) => (
                <button
                  key={k}
                  className={`onb-role${role === k ? ' is-on' : ''}`}
                  onClick={() => setRole(k)}
                >
                  <span className="onb-role__t">{t}</span>
                  <span className="onb-role__d">{d}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="onb-body">
            <h2 className="auth-h">What&apos;s the goal?</h2>
            <div className="onb-roles">
              {GOALS.map((g) => (
                <button
                  key={g}
                  className={`onb-role${goal === g ? ' is-on' : ''}`}
                  onClick={() => {
                    setGoal(g);
                    setStep(2);
                  }}
                >
                  <span className="onb-role__t">{g}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="onb-body">
            <h2 className="auth-h">Current level?</h2>
            <SegmentedControl<Division>
              value={level}
              onChange={setLevel}
              options={[
                { value: 'bronze', label: 'Bronze' },
                { value: 'silver', label: 'Silver' },
                { value: 'gold', label: 'Gold' },
                { value: 'platinum', label: 'Platinum' },
              ]}
            />
            <p className="dim onb-note">
              The diagnostic confirms this and finds your specific bottlenecks. It takes about 14
              minutes.
            </p>
          </div>
        )}

        <div className="onb-foot">
          {step > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          ) : (
            <span />
          )}
          <Button
            variant="primary"
            loading={saving}
            onClick={() => (step < 2 ? setStep(step + 1) : finish())}
          >
            {step < 2 ? 'Continue' : 'Start diagnostic'}
          </Button>
        </div>
      </div>
    </div>
  );
}
